'use client';

import { useEffect, useState, useCallback, RefObject, useRef } from 'react';
import { isTVDevice, TV_KEYS, isKeyPressed } from '../tvDetection';
import { useTVNavigation as useTVNavigationContext } from '../context/TVNavigationContext';
import type { NavigationNode, NavigationDirection } from '../navigation/tvNavigationMap';

/**
 * Hook to detect if the app is running on a TV device
 */
export function useIsTVDevice() {
  const [isTV, setIsTV] = useState(false);

  useEffect(() => {
    setIsTV(isTVDevice());
  }, []);

  return isTV;
}

/**
 * Hook to manage focus for TV navigation
 */
export function useTVFocus(
  ref: RefObject<HTMLElement>,
  options?: {
    autoFocus?: boolean;
    onEnter?: () => void;
    onBack?: () => void;
    onLeft?: () => void;
    onRight?: () => void;
    onUp?: () => void;
    onDown?: () => void;
  }
) {
  const [isFocused, setIsFocused] = useState(false);
  const isTV = useIsTVDevice();

  useEffect(() => {
    const element = ref.current;
    if (!element || !isTV) return;

    // Auto focus on mount if specified
    if (options?.autoFocus) {
      setTimeout(() => {
        element.focus();
      }, 100);
    }

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isKeyPressed(e, TV_KEYS.OK)) {
        e.preventDefault();
        options?.onEnter?.();
      } else if (isKeyPressed(e, TV_KEYS.BACK)) {
        e.preventDefault();
        options?.onBack?.();
      } else if (isKeyPressed(e, TV_KEYS.LEFT)) {
        e.preventDefault();
        options?.onLeft?.();
      } else if (isKeyPressed(e, TV_KEYS.RIGHT)) {
        e.preventDefault();
        options?.onRight?.();
      } else if (isKeyPressed(e, TV_KEYS.UP)) {
        e.preventDefault();
        options?.onUp?.();
      } else if (isKeyPressed(e, TV_KEYS.DOWN)) {
        e.preventDefault();
        options?.onDown?.();
      }
    };

    element.addEventListener('focus', handleFocus);
    element.addEventListener('blur', handleBlur);
    element.addEventListener('keydown', handleKeyDown as any);

    return () => {
      element.removeEventListener('focus', handleFocus);
      element.removeEventListener('blur', handleBlur);
      element.removeEventListener('keydown', handleKeyDown as any);
    };
  }, [ref, isTV, options]);

  return { isFocused, isTV };
}

/**
 * Hook to manage spatial navigation in a grid
 */
export function useTVGrid(options: {
  columns: number;
  rows: number;
  onSelect?: (index: number) => void;
  onBack?: () => void;
  initialFocusIndex?: number;
}) {
  const [focusIndex, setFocusIndex] = useState(options.initialFocusIndex || 0);
  const isTV = useIsTVDevice();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isTV) return;

      const { columns, rows } = options;
      const totalItems = columns * rows;

      if (isKeyPressed(e, TV_KEYS.LEFT)) {
        e.preventDefault();
        setFocusIndex((prev) => {
          const newIndex = prev - 1;
          return newIndex < 0 ? prev : newIndex;
        });
      } else if (isKeyPressed(e, TV_KEYS.RIGHT)) {
        e.preventDefault();
        setFocusIndex((prev) => {
          const newIndex = prev + 1;
          return newIndex >= totalItems ? prev : newIndex;
        });
      } else if (isKeyPressed(e, TV_KEYS.UP)) {
        e.preventDefault();
        setFocusIndex((prev) => {
          const newIndex = prev - columns;
          return newIndex < 0 ? prev : newIndex;
        });
      } else if (isKeyPressed(e, TV_KEYS.DOWN)) {
        e.preventDefault();
        setFocusIndex((prev) => {
          const newIndex = prev + columns;
          return newIndex >= totalItems ? prev : newIndex;
        });
      } else if (isKeyPressed(e, TV_KEYS.OK)) {
        e.preventDefault();
        options.onSelect?.(focusIndex);
      } else if (isKeyPressed(e, TV_KEYS.BACK)) {
        e.preventDefault();
        options.onBack?.();
      }
    },
    [isTV, focusIndex, options]
  );

  useEffect(() => {
    if (!isTV) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTV, handleKeyDown]);

  return {
    focusIndex,
    setFocusIndex,
    isTV,
  };
}

/**
 * Hook to handle video player TV remote controls
 */
export function useTVVideoControls(videoRef: RefObject<HTMLVideoElement>) {
  const isTV = useIsTVDevice();

  useEffect(() => {
    if (!isTV || !videoRef.current) return;

    const video = videoRef.current;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isKeyPressed(e, TV_KEYS.PLAY_PAUSE)) {
        e.preventDefault();
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      } else if (isKeyPressed(e, TV_KEYS.PLAY)) {
        e.preventDefault();
        video.play();
      } else if (isKeyPressed(e, TV_KEYS.PAUSE)) {
        e.preventDefault();
        video.pause();
      } else if (isKeyPressed(e, TV_KEYS.REWIND)) {
        e.preventDefault();
        video.currentTime = Math.max(0, video.currentTime - 10);
      } else if (isKeyPressed(e, TV_KEYS.FAST_FORWARD)) {
        e.preventDefault();
        video.currentTime = Math.min(video.duration, video.currentTime + 10);
      } else if (isKeyPressed(e, TV_KEYS.LEFT)) {
        e.preventDefault();
        video.currentTime = Math.max(0, video.currentTime - 5);
      } else if (isKeyPressed(e, TV_KEYS.RIGHT)) {
        e.preventDefault();
        video.currentTime = Math.min(video.duration, video.currentTime + 5);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTV, videoRef]);

  return { isTV };
}

/**
 * Hook for registering a component node with the navigation system
 */
export function useTVNode(
  nodeId: string,
  neighbors?: Partial<NavigationNode['neighbors']>
): RefObject<HTMLElement> {
  const ref = useRef<HTMLElement>(null);
  const { registerNode, unregisterNode, updateNodeNeighbors } = useTVNavigationContext();
  const isTV = useIsTVDevice();

  useEffect(() => {
    if (!isTV || !ref.current) return;

    // Register the node
    registerNode(nodeId, ref);

    // Update neighbors if provided
    if (neighbors) {
      updateNodeNeighbors(nodeId, neighbors);
    }

    return () => {
      unregisterNode(nodeId);
    };
  }, [isTV, nodeId, registerNode, unregisterNode, updateNodeNeighbors]);

  // Update neighbors when they change
  useEffect(() => {
    if (!isTV || !neighbors) return;
    updateNodeNeighbors(nodeId, neighbors);
  }, [isTV, nodeId, neighbors, updateNodeNeighbors]);

  return ref;
}

/**
 * Hook for registering a row with dynamic cards
 */
export function useTVRow(
  rowId: string,
  rowIndex: number,
  totalCards: number,
  previousRowId?: string,
  nextRowId?: string,
  upTarget?: string
): {
  registerCard: (cardIndex: number, ref: RefObject<HTMLElement>) => void;
  unregisterCard: (cardIndex: number) => void;
  cardRefs: RefObject<HTMLElement>[];
} {
  const { registerNodeWithData, unregisterNode, updateNodeNeighbors } = useTVNavigationContext();
  const isTV = useIsTVDevice();
  const cardRefs = useRef<Map<number, RefObject<HTMLElement>>>(new Map());

  // Register row node
  useEffect(() => {
    if (!isTV) return;

    const rowNode: NavigationNode = {
      id: `${rowId}-row`,
      type: 'row',
      component: 'MovieRow',
      position: {
        zone: 'content',
        rowIndex
      },
      neighbors: {
        up: previousRowId ? `${previousRowId}-row` : upTarget,
        down: nextRowId ? `${nextRowId}-row` : undefined
      },
      metadata: {
        rowId,
        totalCards
      }
    };

    registerNodeWithData(rowNode);

    return () => {
      unregisterNode(`${rowId}-row`);
      // Unregister all cards
      cardRefs.current.forEach((_, cardIndex) => {
        unregisterNode(`${rowId}-card-${cardIndex}`);
      });
      cardRefs.current.clear();
    };
  }, [isTV, rowId, rowIndex, totalCards, previousRowId, nextRowId, upTarget, registerNodeWithData, unregisterNode]);

  const registerCard = useCallback((cardIndex: number, ref: RefObject<HTMLElement>) => {
    if (!isTV || !ref.current) return;

    cardRefs.current.set(cardIndex, ref);

    const cardNode: NavigationNode = {
      id: `${rowId}-card-${cardIndex}`,
      type: 'card',
      component: 'MovieCard',
      position: {
        zone: 'content',
        rowIndex,
        columnIndex: cardIndex
      },
      neighbors: {
        left: cardIndex > 0 ? `${rowId}-card-${cardIndex - 1}` : undefined,
        right: cardIndex < totalCards - 1 ? `${rowId}-card-${cardIndex + 1}` : undefined,
        up: previousRowId 
          ? `${previousRowId}-card-${Math.min(cardIndex, totalCards - 1)}` 
          : upTarget,
        down: nextRowId 
          ? `${nextRowId}-card-${Math.min(cardIndex, totalCards - 1)}` 
          : undefined
      },
      ref,
      metadata: {
        rowId,
        cardIndex,
        isDynamic: true
      }
    };

    registerNodeWithData(cardNode);
  }, [isTV, rowId, rowIndex, totalCards, previousRowId, nextRowId, upTarget, registerNodeWithData]);

  const unregisterCard = useCallback((cardIndex: number) => {
    unregisterNode(`${rowId}-card-${cardIndex}`);
    cardRefs.current.delete(cardIndex);
  }, [rowId, unregisterNode]);

  return {
    registerCard,
    unregisterCard,
    cardRefs: Array.from(cardRefs.current.values())
  };
}

/**
 * Hook for handling keyboard events globally
 * This should be used at the page level to handle TV remote navigation
 */
export function useTVKeyboardNavigation() {
  const { navigate, getCurrentNodeId, engine } = useTVNavigationContext();
  const isTV = useIsTVDevice();

  useEffect(() => {
    if (!isTV) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle back button globally
      if (isKeyPressed(e, TV_KEYS.BACK)) {
        // Check if we're on details page - if so, let the page handle it
        const currentPath = window.location.pathname;
        if (currentPath.includes('/watch/') || currentPath === '/') {
          // Let the page-specific handler take care of it
          // Don't prevent default here to allow page handlers
        } else {
          e.preventDefault();
          e.stopPropagation();

          // Try to focus previous node from history
          const hadPrevious = engine.focusPrevious();

          if (!hadPrevious) {
            // If no history, navigate back in browser
            if (window.history.length > 1) {
              window.history.back();
            } else {
              // If no history, go to home
              window.location.href = '/';
            }
          }

          return;
        }
      }

      // Only handle navigation if we have a current node
      if (!getCurrentNodeId()) return;

      let direction: NavigationDirection | null = null;

      if (isKeyPressed(e, TV_KEYS.UP)) {
        direction = 'up';
      } else if (isKeyPressed(e, TV_KEYS.DOWN)) {
        direction = 'down';
      } else if (isKeyPressed(e, TV_KEYS.LEFT)) {
        direction = 'left';
      } else if (isKeyPressed(e, TV_KEYS.RIGHT)) {
        direction = 'right';
      }

      if (direction) {
        e.preventDefault();
        e.stopPropagation();
        const handled = navigate(direction);
        if (handled) {
          console.log(`[TVNavigation] Navigated ${direction} to ${getCurrentNodeId()}`);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isTV, navigate, getCurrentNodeId, engine]);
}

